// Copyright (c) 2019-2023 Cigent Technology Inc. All Rights Reserved.
import {Component, OnInit} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { D3eService } from 'src/services/d3e-service';
import { HeaderService } from 'src/app/header.service';
import { UntypedFormGroup, Validators, UntypedFormBuilder } from '@angular/forms';
import { IIntegration, PartnerTypes } from 'src/types/integrations';
import { NotificationService } from 'src/services/notification-service';
import { MatDialog } from '@angular/material/dialog';
import { AlertDialogComponent } from 'src/app/dialogs/alert/alert.component';
import { Observable } from 'rxjs';
import {SelectedOrgMethod} from '../../../../shared/selectedOrg';
import {TranslateService} from '@ngx-translate/core';
import { Theme, ThemeService } from '../../../../services/theme.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

@UntilDestroy({ checkProperties: true })
@Component({
    selector: 'app-cyberark',
    templateUrl: './cyberark.component.html',
    styleUrls: ['./cyberark.component.scss'],
    standalone: false
})
export class CyberarkComponent implements OnInit {
  private readonly PARTNER_ID = 2;
  formGroup: UntypedFormGroup;
  integration: IIntegration;
  integrationId: string = null;

  selectedOrgId: string;
  hasUserBeenSet = false;
  theme$: Observable<Theme>;

  fAPIID: string;
  fLogin: string;
  fLoginPHolder: string;
  fUser: string;
  fPass: string;
  fPassPholder: string;
  fLockA: string;
  fLockB: string;
  fLockC: string;
  fAdmin: string;
  fSave: string;
  fCancel: string;
  fValidate: string;
  fConfigCorrect: string;
  fConfigSaved: string;
  fChangesBeingSaved: string;
  fSaveError: string;
  fValidationFailed: string;

  title: string;
  subtitle: string;
  constructor(
    private route: ActivatedRoute,
    private d3eService: D3eService,
    private router: Router,
    private headerService: HeaderService,
    private formBuilder: UntypedFormBuilder,
    private notification: NotificationService,
    public dialog: MatDialog,
    private translate: TranslateService,
    private themeService: ThemeService,
  ) {
    this.translate.get(['INTCB_APPID', 'INTCB_LOGIN', 'INTCB_LOGIN_PHOLDER', 'INTCB_USER', 'INTCB_PASS',
      'INTCB_PASS_PHOLDER', 'INTCB_LOCKA', 'INTCB_LOCKB', 'INTCB_LOCKC', 'INTCB_ADMIN', 'SAVE', 'TITLE_CYBER_INT',
      'CANCEL', 'INTCB_VALIDATE', 'SUBTITLE_CYBER', 'CONFIG_CORRECT', 'CONFIG_SAVED', 'CHANGES_BEING_SAVED',
       'REPORT_SAVE_ERROR', 'ERROR_VALIDATION_FAILED']).subscribe(vals => {
       this.fAPIID = vals['INTCB_APPID'];
       this.fLogin = vals['INTCB_LOGIN'];
       this.fLoginPHolder = vals['INTCB_LOGIN_PHOLDER'];
       this.fUser = vals['INTCB_USER'];
       this.fPass = vals['INTCB_PASS'];
       this.fPassPholder = vals['INTCB_PASS_PHOLDER'];
       this.fLockA = vals['INTCB_LOCKA'];
       this.fLockB = vals['INTCB_LOCKB'];
       this.fLockC = vals['INTCB_LOCKC'];
       this.fAdmin = vals['INTCB_ADMIN'];
       this.fSave = vals['SAVE'];
       this.fCancel = vals['CANCEL'];
       this.fValidate = vals['INTCB_VALIDATE'];
       this.fConfigCorrect = vals['CONFIG_CORRECT'];
       this.fConfigSaved = vals['CONFIG_SAVED'];
       this.fChangesBeingSaved = vals['CHANGES_BEING_SAVED'];
       this.fSaveError = vals['REPORT_SAVE_ERROR'];
       this.fValidationFailed = vals['ERROR_VALIDATION_FAILED'];

       this.title = vals['TITLE_CYBER_INT'];
       this.subtitle = vals['SUBTITLE_CYBER'];
       this.headerService.setTitle(this.title);
       this.headerService.setSubtitle(this.subtitle);
    });
    this.headerService.setIcon('mdi-sync');

    this.formGroup = this.formBuilder.group({
      login_url: ['', Validators.required],
      username: ['', Validators.required],
      password: ['', Validators.required],
      application_id: ['', Validators.required],
      lock_option: ['', Validators.required],
      admin_unlock: [{value: false, disabled: true}],
    });

    this.theme$ = this.themeService.getTheme();
  }

  ngOnInit() {

    this.headerService.orgSelectedChanged
      .pipe(untilDestroyed(this))
      .subscribe(value => {
        this.selectedOrgId = value;
        if ( this.hasUserBeenSet ) {
            // TODO: launch the dirty flag window
            this.router.navigateByUrl('/integrations');
        }
        this.hasUserBeenSet = true;
    });

    this.route.params.subscribe(params => {
      this.integrationId = params.id;
      if (this.integrationId === 'new') {
        this.formGroup.controls['login_url'].setValue('https://login.epm.cyberark.com/EPM/API/Auth/EPM/Logon');
        this.formGroup.controls['application_id'].setValue('Endpoint');
      } else {
        const selectedOrg = SelectedOrgMethod.getSelectedOrg(this.selectedOrgId);

        this.d3eService.get_integration(this.integrationId, selectedOrg)
          .pipe(untilDestroyed(this))
          .subscribe(integration => {
            if (integration.partner_id !== this.PARTNER_ID) {
              this.router.navigateByUrl('/integrations');
            }
            this.formGroup.controls['login_url'].setValue(integration.config.login_url);
            this.formGroup.controls['username'].setValue(integration.config.username);
            this.formGroup.controls['password'].setValue(integration.config.password);
            this.formGroup.controls['application_id'].setValue(integration.config.application_id);
            this.formGroup.controls['lock_option'].setValue(integration.config.lock_option);
            this.formGroup.controls['admin_unlock'].setValue(integration.config.admin_unlock);
            this.integration = integration;
        });
      }
    });
  }

  save() {
    this.notification.showUpdate(this.fChangesBeingSaved);
    const data = this.formGroup.value;
    if (this.integrationId === 'new') {
      const integration: IIntegration = {
        display_name: 'Cyberark',
        enabled: false,
        partner_id: PartnerTypes.CYBERARK_EPM,
        config: {
          login_url: data.login_url,
          application_id: data.application_id,
          username: data.username,
          password: data.password,
          admin_unlock: data.admin_unlock,
          lock_option: data.lock_option,
        }
      };
      const selectedOrg = SelectedOrgMethod.getSelectedOrg(this.selectedOrgId);

      this.d3eService.add_integration(integration, selectedOrg)
        .subscribe(() => {
          this.reportSaveSuccess();
          this.goToIntegrations();
        }, this.reportSaveError.bind(this));
    } else {
      this.integration.config = {
        login_url: data.login_url,
        application_id: data.application_id,
        username: data.username,
        password: data.password,
        admin_unlock: data.admin_unlock || false,
        lock_option: data.lock_option,
    };
      const selectedOrg = SelectedOrgMethod.getSelectedOrg(this.selectedOrgId);

      this.d3eService.update_integration(this.integration, selectedOrg)
        .subscribe(() => {
          this.reportSaveSuccess();
          this.goToIntegrations();
        }, this.reportSaveError.bind(this));
    }
  }

  reportSaveSuccess() {
    this.notification.showSuccess(this.fConfigSaved);
  }

  reportSaveError() {
    this.notification.showError(this.fSaveError);
  }

  goToIntegrations() {
    this.router.navigateByUrl('/integrations');
  }

  cancel() {
    if (this.formGroup.touched) {
      const dialogRef = this.dialog.open(AlertDialogComponent, {
        width: '450px',
        data: {
          title: this.translate.instant('DISCARD_CHANGES'),
          content: this.translate.instant('DISCARD_CHANGES_CONTENT'),
          rejectBtnName: this.translate.instant('NO'),
          acceptBtnName: this.translate.instant('YES'),
          showRejectBtn: true,
        },
      });
      dialogRef.afterClosed().subscribe(result => {
        if (result) {
          this.goToIntegrations();
        }
      });
    } else {
      this.goToIntegrations();
    }
  }

  validate() {
    const data = this.formGroup.value;
    const validate$ = this.d3eService.validate_cyberark_config(
        data.login_url, data.username, data.password, data.application_id);
    validate$
      .subscribe(response => {
        if (response.error) {
          this.notification.showError(this.fValidationFailed);
        } else if (response.status_code && response.status_code !== 200) {
          this.notification.showError(this.fValidationFailed);
        } else if (response.status_code && response.status_code === 200) {
          this.notification.showSuccess(this.fConfigCorrect);
        }
    });
  }

}
